import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import dayjs from "dayjs";
import { saveAs } from "file-saver";
import {
	Box,
	Button,
	Chip,
	Collapse,
	IconButton,
	InputAdornment,
	Paper,
	Skeleton,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TablePagination,
	TableRow,
	TableSortLabel,
	TextField,
	Tooltip,
	Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
	ArrowDownwardRounded,
	ArrowUpwardRounded,
	AssignmentOutlined,
	AssignmentTurnedInOutlined,
	CircleOutlined,
	ErrorOutlineRounded,
	ExpandMoreRounded,
	FileDownloadOutlined,
	InfoOutlined,
	ReportOutlined,
	Search,
	SupportAgentOutlined,
	VisibilityOutlined,
	WarningAmberRounded,
} from "@mui/icons-material";
import { useCentralAlertas } from "../context";
import TimelineIncidente from "../timelineIncidente";
import IconeTipoAlerta from "../iconeTipoAlerta";
import {
	LABEL_CATEGORIA_INTELIGENTE,
	LABEL_SEVERIDADE,
	LABEL_STATUS_INCIDENTE,
		LABEL_TIPO_EVENTO,
	SEVERIDADES,
	formatarDuracaoCurta,
} from "../../../utils/centralDeAlertas/labels";
import {
	corSeveridade,
	corStatusIncidente,
	corTipoEvento,
} from "../../../utils/centralDeAlertas/cores";
import useStyles from "./styles";
import { GUIDES_CENTRAL, MetricInfo } from "../metricInfo";

// Tabela de incidentes da Central de Alertas: correlação alerta ->
// atendimento -> OS com busca local, ordenação por coluna, linhas
// expansíveis (TimelineIncidente), exportação CSV pt-BR (; como separador)
// e foco vindo da TimelineGlobal (paginação + scroll + destaque de 2s).

const LINHAS_POR_PAGINA = 20;
const DURACAO_DESTAQUE_MS = 2000;
const ATRASO_SCROLL_MS = 120;
const LINHAS_SKELETON = 6;
const TOTAL_COLUNAS = 7;

const ICONE_SEVERIDADE = {
	critico: WarningAmberRounded,
	alto: ErrorOutlineRounded,
	medio: ReportOutlined,
	baixo: InfoOutlined,
	informativo: CircleOutlined,
};

const COLUNAS_ORDENAVEIS = [
	{ id: "severidade", rotulo: "Severidade" },
	{ id: "alerta", rotulo: "Alerta · equipamento" },
	{ id: "cliente", rotulo: "Cliente" },
	{ id: "inicio", rotulo: "Início" },
	{ id: "duracao", rotulo: "Duração" },
];

// Marcos da coluna "Linha do tempo" — MESMA ordem, ícones e cores da coluna
// homônima da Análise de padrão (e da linha do tempo do incidente): colorido
// quando o registro existe, esmaecido quando não; rótulo no Tooltip (nunca
// depender só da cor).
function marcosDoIncidente(theme, incidente) {
	return [
		{
			chave: "queda",
			rotulo: LABEL_TIPO_EVENTO.queda,
			presente: true,
			Icone: ArrowDownwardRounded,
			cor: corTipoEvento(theme, "queda"),
		},
		{
			chave: "reconhecimento",
			rotulo: "Reconhecimento",
			presente: Boolean(incidente.alerta?.reconhecido),
			Icone: VisibilityOutlined,
			cor: corStatusIncidente(theme, "reconhecido"),
		},
		{
			chave: "atendimento_aberto",
			rotulo: LABEL_TIPO_EVENTO.atendimento_aberto,
			presente: Boolean(incidente.atendimento),
			Icone: SupportAgentOutlined,
			cor: corTipoEvento(theme, "atendimento_aberto"),
		},
		{
			chave: "os_criada",
			rotulo: LABEL_TIPO_EVENTO.os_criada,
			presente: Boolean(incidente.os),
			Icone: AssignmentOutlined,
			cor: corTipoEvento(theme, "os_criada"),
		},
		{
			chave: "os_concluida",
			rotulo: LABEL_TIPO_EVENTO.os_concluida,
			presente: Boolean(incidente.os?.dataHoraConclusao),
			Icone: AssignmentTurnedInOutlined,
			cor: corTipoEvento(theme, "os_concluida"),
		},
		{
			chave: "recuperacao",
			rotulo: LABEL_TIPO_EVENTO.recuperacao,
			presente: Boolean(incidente.recuperacao),
			Icone: ArrowUpwardRounded,
			cor: corTipoEvento(theme, "recuperacao"),
		},
	];
}

function valorOrdenacao(incidente, coluna) {
	switch (coluna) {
		case "severidade":
			return SEVERIDADES.indexOf(incidente.severidade);
		case "alerta":
			return incidente.alerta?.titulo || "";
		case "cliente":
			return incidente.cliente?.nome || "";
		case "equipamento":
			return incidente.equipamento?.nome || "";
		case "inicio":
			return dayjs(incidente.dataHoraQueda).valueOf();
				case "duracao":
			return duracaoMinutos(incidente);
		case "recuperacao":
			// null enquanto o incidente está aberto — a ordenação joga por último.
			return incidente.recuperacao
				? dayjs(incidente.recuperacao).valueOf()
				: null;
		default:
			return 0;
	}
}

// Em aberto: tempo desde a queda; encerrado: MTTR (ou queda → recuperação).
function duracaoMinutos(incidente) {
	if (incidente.aberto || !incidente.recuperacao) {
		return dayjs().diff(dayjs(incidente.dataHoraQueda), "minute", true);
	}
	return (
		incidente.mttr ??
		dayjs(incidente.recuperacao).diff(dayjs(incidente.dataHoraQueda), "minute", true)
	);
}

// Ordenação estável com null/undefined sempre por último, independente da
// direção. Incidentes ABERTOS vêm sempre no topo, qualquer que seja a coluna
// e a direção escolhidas — a ordenação do usuário vale dentro de cada grupo.
function ordenarIncidentes(incidentes, orderBy, order) {
	const direcao = order === "asc" ? 1 : -1;
	return (incidentes || [])
		.slice()
		.sort((a, b) => {
			if (Boolean(a.aberto) !== Boolean(b.aberto)) {
				return a.aberto ? -1 : 1;
			}
			const valorA = valorOrdenacao(a, orderBy);
			const valorB = valorOrdenacao(b, orderBy);
			const vazioA = valorA === null || valorA === undefined;
			const vazioB = valorB === null || valorB === undefined;
			if (vazioA && vazioB) return 0;
			if (vazioA) return 1;
			if (vazioB) return -1;
			if (typeof valorA === "string") {
				return valorA.localeCompare(valorB, "pt-BR") * direcao;
			}
			return (valorA - valorB) * direcao;
		});
}

// Escapa campo CSV que contenha ; aspas ou quebra de linha (aspas dobradas).
function escaparCampoCsv(valor) {
	const texto = valor === null || valor === undefined ? "" : String(valor);
	if (/[;"\n\r]/.test(texto)) {
		return `"${texto.replace(/"/g, '""')}"`;
	}
	return texto;
}

const TabelaIncidentes = () => {
	const theme = useTheme();
	const styles = useStyles(theme);
	const {
		carregando,
		incidentesFiltrados,
		focoIncidenteId,
		limparFoco,
		janelaCorrelacaoMinutos,
		janelaAgrupamentoMinutos,
		marcarAlertasReconhecidos,
		tipoErp,
		erpAplicado,
	} = useCentralAlertas();

	const [busca, setBusca] = useState("");
	const [orderBy, setOrderBy] = useState("inicio");
	const [order, setOrder] = useState("desc");
	const [pagina, setPagina] = useState(0);
	const [expandidos, setExpandidos] = useState(() => new Set());
	const [destacadoId, setDestacadoId] = useState(null);

	const linhasRef = useRef(new Map());
	// Página pedida pelo foco da TimelineGlobal — tem prioridade sobre o
	// reset de página disparado pela limpeza da busca no mesmo ciclo.
	const paginaFocoRef = useRef(null);
	const timerScrollRef = useRef(null);
	const timerDestaqueRef = useRef(null);

	// Busca textual local, case-insensitive, sobre campos e rótulos pt-BR.
	const incidentesBuscados = useMemo(() => {
		const termo = busca.trim().toLowerCase();
		if (!termo) return incidentesFiltrados;
		return incidentesFiltrados.filter((incidente) => {
			const campos = [
				incidente.cliente?.nome,
				incidente.equipamento?.nome,
				incidente.alerta?.titulo,
				LABEL_SEVERIDADE[incidente.severidade],
				LABEL_STATUS_INCIDENTE[incidente.status],
			];
			return campos.some((campo) =>
				(campo || "").toLowerCase().includes(termo)
			);
		});
	}, [incidentesFiltrados, busca]);

	const incidentesOrdenados = useMemo(
		() => ordenarIncidentes(incidentesBuscados, orderBy, order),
		[incidentesBuscados, orderBy, order]
	);

	const totalBuscados = incidentesBuscados.length;

	// Volta à primeira página quando busca, ordenação ou filtros globais
	// mudam — exceto quando a mudança veio do foco (que define a página).
	useEffect(() => {
		if (paginaFocoRef.current !== null) {
			setPagina(paginaFocoRef.current);
			return;
		}
		setPagina(0);
	}, [totalBuscados, orderBy, order]);

	// Foco vindo da TimelineGlobal: limpa a busca, pagina até o incidente,
	// expande a linha, rola até ela e aplica destaque temporário.
	useEffect(() => {
		if (!focoIncidenteId) return;
		setBusca("");
		const listaSemBusca = ordenarIncidentes(incidentesFiltrados, orderBy, order);
		const indice = listaSemBusca.findIndex(
			(incidente) => incidente.id === focoIncidenteId
		);
		if (indice === -1) {
			limparFoco();
			return;
		}
		const paginaAlvo = Math.floor(indice / LINHAS_POR_PAGINA);
		paginaFocoRef.current = paginaAlvo;
		setPagina(paginaAlvo);
		setExpandidos((atuais) => {
			const proximos = new Set(atuais);
			proximos.add(focoIncidenteId);
			return proximos;
		});
		setDestacadoId(focoIncidenteId);
		clearTimeout(timerScrollRef.current);
		clearTimeout(timerDestaqueRef.current);
		timerScrollRef.current = setTimeout(() => {
			paginaFocoRef.current = null;
			const linha = linhasRef.current.get(focoIncidenteId);
			if (linha) {
				linha.scrollIntoView({ behavior: "smooth", block: "center" });
			}
		}, ATRASO_SCROLL_MS);
		timerDestaqueRef.current = setTimeout(
			() => setDestacadoId(null),
			DURACAO_DESTAQUE_MS
		);
		limparFoco();
	}, [focoIncidenteId, incidentesFiltrados, orderBy, order, limparFoco]);

	useEffect(
		() => () => {
			clearTimeout(timerScrollRef.current);
			clearTimeout(timerDestaqueRef.current);
		},
		[]
	);

	const alternarOrdenacao = (coluna) => {
		if (orderBy === coluna) {
			setOrder((atual) => (atual === "asc" ? "desc" : "asc"));
			return;
		}
		setOrderBy(coluna);
		setOrder(coluna === "inicio" ? "desc" : "asc");
	};

	const alternarExpansao = (incidenteId) => {
		setExpandidos((atuais) => {
			const proximos = new Set(atuais);
			if (proximos.has(incidenteId)) proximos.delete(incidenteId);
			else proximos.add(incidenteId);
			return proximos;
		});
	};

	const registrarLinha = (incidenteId) => (no) => {
		if (no) linhasRef.current.set(incidenteId, no);
		else linhasRef.current.delete(incidenteId);
	};

	// Exporta as linhas filtradas e ordenadas atuais (todas as páginas) em
	// CSV pt-BR com ; como separador e BOM para acentuação no Excel.
	const exportarCsv = () => {
		const cabecalho = [
			"Severidade",
			"Alerta",
			"Cliente",
			"Equipamento",
			"Início",
			"Recuperação",
			"Status",
			"Categorias",
		];
		const linhas = incidentesOrdenados.map((incidente) => [
			LABEL_SEVERIDADE[incidente.severidade] || incidente.severidade,
			incidente.alerta?.titulo || "—",
			incidente.cliente?.nome || "—",
			incidente.equipamento?.nome || "—",
			dayjs(incidente.dataHoraQueda).format("DD/MM/YYYY HH:mm"),
			incidente.recuperacao
				? dayjs(incidente.recuperacao).format("DD/MM/YYYY HH:mm")
				: "—",
			LABEL_STATUS_INCIDENTE[incidente.status] || incidente.status,
			(incidente.categoriasInteligentes || [])
				.map((categoria) => LABEL_CATEGORIA_INTELIGENTE[categoria] || categoria)
				.join(", "),
		]);
		const conteudo = [cabecalho, ...linhas]
			.map((linha) => linha.map(escaparCampoCsv).join(";"))
			.join("\r\n");
		const blob = new Blob(["\ufeff" + conteudo], {
			type: "text/csv;charset=utf-8",
		});
		saveAs(blob, "incidentes-central-de-alertas.csv");
	};

	const totalPaginas = Math.max(
		1,
		Math.ceil(incidentesOrdenados.length / LINHAS_POR_PAGINA)
	);
	const paginaAtual = Math.min(pagina, totalPaginas - 1);
	const incidentesPagina = incidentesOrdenados.slice(
		paginaAtual * LINHAS_POR_PAGINA,
		paginaAtual * LINHAS_POR_PAGINA + LINHAS_POR_PAGINA
	);

	if (carregando) {
		return (
			<Paper sx={styles.container}>
				<Box sx={styles.linhaCabecalho}>
					<Box sx={styles.cabecalho}>
						<Typography variant="mySubtitle">Incidentes</Typography>
						<Typography variant="caption" sx={styles.legenda}>
							Correlação alerta → atendimento → OS
						</Typography>
					</Box>
					<MetricInfo theme={theme} guide={GUIDES_CENTRAL.tabelaIncidentes} />
				</Box>
				<Box sx={styles.skeletonBarra}>
					<Skeleton variant="rounded" sx={styles.skeletonBusca} />
					<Skeleton variant="rounded" sx={styles.skeletonBotao} />
				</Box>
				<Box sx={styles.skeletonConteudo}>
					<Skeleton variant="rounded" sx={styles.skeletonCabecalhoTabela} />
					{Array.from({ length: LINHAS_SKELETON }).map((_, indice) => (
						<Skeleton
							key={`skeleton-linha-${indice}`}
							variant="rounded"
							sx={styles.skeletonLinha}
						/>
					))}
				</Box>
			</Paper>
		);
	}

	return (
		<Paper sx={styles.container}>
			<Box sx={styles.linhaCabecalho}>
				<Box sx={styles.cabecalho}>
					<Typography variant="mySubtitle">Incidentes</Typography>
					<Typography variant="caption" sx={styles.legenda}>
						Correlação alerta → atendimento → OS
					</Typography>
				</Box>
				<MetricInfo theme={theme} guide={GUIDES_CENTRAL.tabelaIncidentes} />
			</Box>
			<Box sx={styles.barraAcoes}>
				<TextField
					size="small"
					value={busca}
					onChange={(evento) => setBusca(evento.target.value)}
					placeholder="Buscar por cliente, equipamento, título…"
					sx={styles.campoBusca}
					inputProps={{ "aria-label": "Buscar incidentes" }}
					InputProps={{
						startAdornment: (
							<InputAdornment position="start">
								<Search fontSize="small" sx={styles.iconeBusca} />
							</InputAdornment>
						),
					}}
				/>
				<Button
					variant="outlined"
					size="small"
					startIcon={<FileDownloadOutlined />}
					onClick={exportarCsv}
					disabled={!incidentesOrdenados.length}
					sx={styles.botaoExportar}
				>
					Exportar CSV
				</Button>
			</Box>
			<TableContainer sx={styles.containerTabela}>
				<Table size="small" sx={styles.tabela}>
					<TableHead>
						<TableRow>
							{COLUNAS_ORDENAVEIS.map((coluna) => (
								<TableCell
									key={coluna.id}
									sx={styles.celulaCabecalho}
									sortDirection={orderBy === coluna.id ? order : false}
								>
									<TableSortLabel
										active={orderBy === coluna.id}
										direction={orderBy === coluna.id ? order : "asc"}
										onClick={() => alternarOrdenacao(coluna.id)}
									>
										{coluna.rotulo}
									</TableSortLabel>
								</TableCell>
							))}
							<TableCell sx={styles.celulaCabecalho}>Ciclo</TableCell>
							<TableCell sx={styles.celulaCabecalho} />
						</TableRow>
					</TableHead>
					<TableBody>
						{!incidentesOrdenados.length && (
							<TableRow>
								<TableCell colSpan={TOTAL_COLUNAS} sx={styles.celulaVazia}>
									<Typography color="text.secondary">
										{busca.trim()
											? "Nenhum incidente encontrado para a busca."
											: "Nenhum incidente no período selecionado."}
									</Typography>
								</TableCell>
							</TableRow>
						)}
						{incidentesPagina.map((incidente) => {
							const expandido = expandidos.has(incidente.id);
							const IconeSeveridade =
								ICONE_SEVERIDADE[incidente.severidade] || CircleOutlined;
							return (
								<Fragment key={incidente.id}>
									<TableRow
										hover
										ref={registrarLinha(incidente.id)}
										onClick={() => alternarExpansao(incidente.id)}
										sx={styles.linha(
											destacadoId === incidente.id,
											incidente.aberto ? corSeveridade(theme, incidente.severidade) : null,
										)}
									>
										<TableCell sx={styles.celulaNowrap}>
											<Chip
												size="small"
												icon={<IconeSeveridade />}
												label={
													LABEL_SEVERIDADE[incidente.severidade] ||
													incidente.severidade
												}
												sx={styles.chipTabela(
													corSeveridade(theme, incidente.severidade)
												)}
											/>
										</TableCell>
										<TableCell sx={styles.celulaAlerta}>
											<Box sx={styles.linhaTituloAlerta}>
												{/* Ícone do padrão global do catálogo (mesmo da aba
												    Exibição), com o TIPO do alerta no Tooltip; a cor
												    carrega a severidade do alerta. */}
												<IconeTipoAlerta
													chave={incidente.alerta?.tipoAlertaChave}
													nome={incidente.alerta?.tipoAlertaNome}
													sx={{
														color: corSeveridade(theme, incidente.severidade),
													}}
												/>
												<Box sx={styles.textosAlerta}>
													<Tooltip title={incidente.alerta?.titulo || ""} arrow>
														<Typography variant="body2" sx={styles.textoAlerta}>
															{incidente.alerta?.titulo || "—"}
														</Typography>
													</Tooltip>
													<Typography variant="caption" sx={styles.textoEquipamento}>
														{incidente.equipamento?.nome || "—"}
													</Typography>
												</Box>
											</Box>
										</TableCell>
										<TableCell sx={styles.celulaCliente}>
											<Tooltip title={incidente.cliente?.nome || ""} arrow>
												<Typography variant="caption" sx={styles.textoCliente}>
													{incidente.cliente?.nome || "—"}
												</Typography>
											</Tooltip>
										</TableCell>
										<TableCell sx={styles.celulaNumero}>
											{dayjs(incidente.dataHoraQueda).format("DD/MM HH:mm")}
										</TableCell>
										<TableCell sx={styles.celulaNumero}>
											<Tooltip
												title={
													incidente.recuperacao
														? `Recuperação: ${dayjs(incidente.recuperacao).format("DD/MM HH:mm")}`
														: "Sem recuperação"
												}
												arrow
											>
												<Typography
													component="span"
													sx={styles.textoDuracao(
														incidente.aberto ? corSeveridade(theme, "critico") : null,
													)}
												>
													{incidente.aberto
														? `há ${formatarDuracaoCurta(duracaoMinutos(incidente))}`
														: formatarDuracaoCurta(duracaoMinutos(incidente))}
												</Typography>
											</Tooltip>
										</TableCell>
										<TableCell sx={styles.celulaCiclo}>
											{/* Ciclo em segmentos: cheio quando registrado,
											    apagado quando não — rótulo no Tooltip. */}
											<Box sx={styles.barraCiclo}>
												{marcosDoIncidente(theme, incidente).map(
													({ chave, rotulo, presente, cor }) => (
														<Tooltip
															key={chave}
															title={
																presente
																	? rotulo
																	: `${rotulo} — não registrado`
															}
															arrow
														>
															<Box sx={styles.segmentoCiclo(presente ? cor : null)} />
														</Tooltip>
													)
												)}
											</Box>
										</TableCell>
										<TableCell sx={styles.celulaAcoes}>
											<IconButton
												size="small"
												aria-label={
													expandido
														? "Recolher detalhes do incidente"
														: "Expandir detalhes do incidente"
												}
												onClick={(evento) => {
													evento.stopPropagation();
													alternarExpansao(incidente.id);
												}}
											>
												<ExpandMoreRounded
													sx={styles.iconeExpandir(expandido)}
												/>
											</IconButton>
										</TableCell>
									</TableRow>
									<TableRow>
										<TableCell
											colSpan={TOTAL_COLUNAS}
											sx={styles.celulaExpansao}
										>
											<Collapse in={expandido} timeout="auto" unmountOnExit>
												<Box sx={styles.boxExpansao}>
													<TimelineIncidente
														incidente={incidente}
														janelaCorrelacaoMinutos={janelaCorrelacaoMinutos}
														janelaAgrupamentoMinutos={janelaAgrupamentoMinutos}
														aoReconhecerAlertas={marcarAlertasReconhecidos}
														erp={tipoErp}
														erpId={erpAplicado}
													/>
												</Box>
											</Collapse>
										</TableCell>
									</TableRow>
								</Fragment>
							);
						})}
					</TableBody>
				</Table>
			</TableContainer>
			{incidentesOrdenados.length > 0 && (
				<TablePagination
					component="div"
					count={incidentesOrdenados.length}
					page={paginaAtual}
					onPageChange={(evento, novaPagina) => setPagina(novaPagina)}
					rowsPerPage={LINHAS_POR_PAGINA}
					rowsPerPageOptions={[LINHAS_POR_PAGINA]}
					labelRowsPerPage="Linhas por página"
					labelDisplayedRows={({ from, to, count }) =>
						`${from}–${to} de ${count}`
					}
					getItemAriaLabel={(tipo) =>
						tipo === "next" ? "Próxima página" : "Página anterior"
					}
					sx={styles.paginacao}
				/>
			)}
		</Paper>
	);
};

export default TabelaIncidentes;
